import { Injectable, inject, signal, computed, Signal } from "@angular/core";
import { EMPTY, Observable, of, throwError } from "rxjs";
import { tap, catchError, map, switchMap, finalize } from "rxjs/operators";
import { ScheduleService } from "@core/services";
import {
  CreateScheduleRequest,
  SaveScheduleChangesRequest,
  ScheduleItem,
  ShiftStatusCheckRequest,
  ModifyScheduleLockRequest,
  ModifyScheduleLockResponse,
  ResponseCode,
} from "@core/types";
import { rxResource } from "@angular/core/rxjs-interop";

@Injectable({ providedIn: "root" })
export class ScheduleStore {
  //====================================================
  // Dependencies
  //====================================================
  private readonly scheduleService = inject(ScheduleService);

  //====================================================
  // State
  //====================================================
  private readonly _schedules = signal<ScheduleItem[]>([]);
  private readonly _activeScheduleItem = signal<ScheduleItem | null>(null);
  private readonly _isScheduleLocked = signal(false);
  private readonly _loading = signal(false);

  //====================================================
  // Selectors
  //====================================================
  readonly schedules = this._schedules.asReadonly();
  readonly activeScheduleItem = this._activeScheduleItem.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly isScheduleLocked = this._isScheduleLocked.asReadonly();
  readonly scheduleStats = computed(() => {
    let gaps = 0;
    let overlaps = 0;

    for (const s of this._schedules()) {
      if (s.shiftStatus === 2) gaps++;
      else if (s.shiftStatus === 1) overlaps++;
    }

    return { gaps, overlaps };
  });

  readonly gaps = computed(() => this.scheduleStats().gaps);
  readonly overlaps = computed(() => this.scheduleStats().overlaps);

  //====================================================
  // Read Operations
  //====================================================
  loadSchedules(organizationId: number, scheduleGroupId: number, startDateTime: string, endDateTime: string): Observable<ScheduleItem[]> {
    this._loading.set(true);
    return this.scheduleService.getSchedules(organizationId, scheduleGroupId, startDateTime, endDateTime).pipe(
      tap((res) => {
        if (res.isSuccess && res.data) this._schedules.set(res.data);
      }),
      map((res) => res.data ?? []),
      finalize(() => this._loading.set(false)),
    );
  }

  //====================================================
  // Write Operations
  //====================================================
  checkShiftStatus(params: ShiftStatusCheckRequest): Observable<number | null> {
    this._loading.set(true);
    return this.scheduleService.shiftStatus(params).pipe(
      map((res) => (res.isSuccess ? (res.data ?? null) : null)),
      finalize(() => this._loading.set(false)),
    );
  }

  createSchedule(payload: CreateScheduleRequest): Observable<number> {
    this._loading.set(true);
    return this.scheduleService.createSchedule(payload).pipe(
      map((res) => res.data ?? 0),
      finalize(() => this._loading.set(false)),
    );
  }

  updateSchedule(payload: CreateScheduleRequest): Observable<number> {
    this._loading.set(true);
    return this.scheduleService.updateSchedule(payload).pipe(
      map((res) => res.data ?? 0),
      finalize(() => this._loading.set(false)),
    );
  }

  saveChanges(updates: string, deletes: string): Observable<number> {
    this._loading.set(true);
    return this.scheduleService.saveScheduleChanges({ updates, deletes }).pipe(
      map((res) => res.data ?? 0),
      finalize(() => this._loading.set(false)),
    );
  }

  modifyLock(scheduleGroupId: number, lockType: number): Observable<ModifyScheduleLockResponse> {
    this._loading.set(true);
    return this.scheduleService.modifyScheduleLock({ scheduleGroupId, lockType }).pipe(
      tap((res) => {
        this._isScheduleLocked.set(!res.isSuccess && res.code === ResponseCode.PERMISSION_DENIED);
      }),
      map((res) => res.data ?? { scheduleGroupId, statEmployeeId: 0, lastModified: "", lock: 0 }),
      finalize(() => this._loading.set(false)),
    );
  }

  //====================================================
  // Helpers
  //====================================================
  // setSchedules(schedules: ScheduleItem[]): void {
  //   this._schedules.set(schedules);
  // }

  removeLocalSchedules(ids: number[]): void {
    this._schedules.update((s) => s.filter((item) => !ids.includes(item.scheduleItemID)));
  }

  updateLocalScheduleItem(id: number, updates: Partial<ScheduleItem>): void {
    this._schedules.update((s) => s.map((item) => (item.scheduleItemID === id ? { ...item, ...updates } : item)));
  }

  sortSchedules(compareFn: (a: ScheduleItem, b: ScheduleItem) => number): void {
    this._schedules.update((s) => [...s].sort(compareFn));
  }

  clearSchedules(): void {
    this._schedules.set([]);
  }

  setActiveScheduleItem(item: ScheduleItem | null): void {
    this._activeScheduleItem.set(item);
  }
}
